<template>
<w-settings-content-template>
  <div slot="content">
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h3 mr-r15">관리 대상 목록</div>
        <el-button class="mr-r15" size="mini" :disabled="checkedRows.length == 0" @click="clickDeleteServerButton">장비 삭제</el-button>
        <span class="mr-r5">관리여부</span>
        <div class="setting-addel">        
          <el-button size="mini" @click="AllCheckChangeButtonClick('1')"><i class="fas fa-check-square"></i></el-button>
          <el-button size="mini" @click="AllCheckChangeButtonClick('0')"><i class="far fa-square"></i></el-button>
        </div>
      </div>
      <div class="vertical-center-box">
        <el-checkbox v-model="lastThreeDaysFlag" @change="loadData">최근 3일 이내 등록된 장비 <em>{{recentRegisteredObjCount}}개</em></el-checkbox>
        <el-button class="mr-r4" type="primary" size="mini" @click="clickSaveButton" :disabled="!cellEditing.isModified">저장</el-button>
        <el-button class="mr-r4" size="mini" @click="clickCancelButton" :disabled="!cellEditing.isModified">취소</el-button>
        <w-header-pagination class="mr-r4" :pagination="pagination" @current-change="handlerHeaderCurrentChange"></w-header-pagination>
        <w-header-search-button size="mini" @click="showSearchTool = !showSearchTool"></w-header-search-button>
      </div>
    </el-row>
    <el-row>
      <el-table-wrapper v-loading="isLoading" :editable="true" :data="tableData" :columns="tableColumns" :column-default="columnDefault" ref="emsObjTable"
        @selection-change="changeCheckedRow" :pagination="true" :show-search-tool="showSearchTool" :show-custom-header="true" @condition-change="loadData"
        @pagination-change="handlerPaginationChange"
        @total-change="handlerPaginationChange"
        @cell-dblclick="startEdit"
        remote :total="total">
        <template slot-scope="scope" slot="switch-slot">
          <w-switch-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" active-value="1" inactive-value="0" @cell-edit-end="editTwoStatusCell" />
        </template>
        <template slot-scope="scope" slot="input-slot">
          <w-input-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" @cell-edit-end="endEdit" />
        </template>
        <template slot-scope="scope" slot="checkbox-slot">
          <w-checkbox-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" true-label="1" false-label="0" @cell-edit-end="editTwoStatusCell" />
        </template>
        <template slot-scope="scope" slot="rsc-rule-slot">
          <w-select-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" :list="rscEventRuleList"
            keyField="ruleTmplId" labelField="ruleTmplName" valueField="ruleTmplId" @cell-edit-end="endEdit" />
        </template>
        <template slot-scope="scope" slot="event-rule-slot">
          <w-select-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" :list="eventAlarmRuleList"
            keyField="ruleTmplId" labelField="ruleTmplName" valueField="ruleTmplId" @cell-edit-end="endEdit"/>
        </template>
      </el-table-wrapper>
    </el-row>
  </div>
</w-settings-content-template>
</template>

<script>
import { cloneDeep, isNil } from 'lodash';

import { columnDefault, defaultPagination } from '@/constants/table';
import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import msgUtil from '@/utils/message';

import operationSettingsApi from '@/api/settings/common/operation-settings';
import ruleApi from '@/api/settings/common/rule';
import objtypeApi from '@/api/model/objtype';

import CellEditingMixin from '@/mixins/table/cell-editing';
import WInputEditor from '@/components/table/cell-editor/InputEditor';
import WSelectEditor from '@/components/table/cell-editor/SelectEditor';
import WCheckboxEditor from '@/components/table/cell-editor/CheckboxEditor';
import WSwitchEditor from '@/components/table/cell-editor/SwitchEditor';
import WSettingsContentTemplate from '@/components/template/SettingsContentTemplate';
import WHeaderPagination from '@/components/table/HeaderPagination';

const pagination = cloneDeep(defaultPagination);
const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);
const dateFormatter = (row, column, value) => (isNil(value) ? EMPTY_TEXT : getDateToString(value));

export default {
  name: 'WObjSetting',
  mixins: [CellEditingMixin],
  components: {
    WSettingsContentTemplate,
    WHeaderPagination,
    WInputEditor,
    WSelectEditor,
    WCheckboxEditor,
    WSwitchEditor,
  },
  props: {
    activeTab: String,
  },
  created() {
    ruleApi.getRscEventRules().then((response) => {
      this.rscEventRuleList = response.data.data;
    });
    ruleApi.getEventAlarmRulesByManagetypeId('EMS').then((response) => {
      this.eventAlarmRuleList = response.data.data;
    });
    objtypeApi.getObjtypeList('EMS').then((response) => {
      this.objtypeList = response.data.data;
    });
  },
  mounted() {
    this.loadData();
  },
  data() {
    return {
      pagination,
      isLoading: false,
      columnDefault,
      total: 0,
      type: 'rsctype',
      showSearchTool: false,
      selectedRow: null,
      tableData: [],
      checkedRows: [],
      lastThreeDaysFlag: false,
      rscEventRuleList: [],
      eventAlarmRuleList: [],
      objtypeList: [],
      recentRegisteredObjCount: null,
      tableColumns: [
        {
          align: 'center',
          type: 'selection',
        },
        {
          prop: 'objManagementApplyFlag',
          label: '관리여부',
          editable: true,
          scopedSlot: 'switch-slot',
          width: 90,
          sortable: true,
          filters: [
            {
              text: '관리',
              value: '1',
            },
            {
              text: '비관리',
              value: '0',
            },
          ],
        },
        {
          prop: 'objName',
          label: '장비명',
          editable: true,
          scopedSlot: 'input-slot',
          width: 250,
          sortable: true,
          searchable: true,
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          formatter: defaultFormatter,
          sortable: true,
          searchable: true,
        },
        {
          prop: 'objtypeName',
          label: '장비종류',
          formatter: defaultFormatter,
          searchable: true,
        },
        {
          prop: 'eventExceptFlag',
          label: '이벤트 제외',
          editable: true,
          scopedSlot: 'checkbox-slot',
          width: 100,
          align: 'center',
        },
        {
          prop: 'rscEventRuleId',
          label: '수집/판단정책',
          editable: true,
          scopedSlot: 'rsc-rule-slot',
          width: 230,
          searchable: true,
          remoteProp: 'rscrt.rule_tmpl_name',
        },
        {
          prop: 'eventAlarmRuleId',
          label: '이벤트/판단정책',
          editable: true,
          scopedSlot: 'event-rule-slot',
          width: 230,
          searchable: true,
          remoteProp: 'eventrt.rule_tmpl_name',
        },
        {
          prop: 'registDatetime',
          label: '장비 등록일',
          width: 150,
          formatter: dateFormatter,
          searchable: true,
          sortable: true,
          searchType: 'date',
        },
      ],
    };
  },
  methods: {
    getValueAfterCheckEmpty,
    loadData() {
      const tableParams = this.$refs.emsObjTable.getParameter();
      const params = {
        lastThreeDaysFlag: this.lastThreeDaysFlag,
        ...tableParams,
      };
      operationSettingsApi.getRecentRegisteredEmsObjCount().then((response) => {
        this.recentRegisteredObjCount = response.data.data;
      });
      this.isLoading = true;
      operationSettingsApi.getEmsObjList(params).then((response) => {
        this.total = response.data.total;
        const data = cloneDeep(response.data.data);
        this.tableData = this.getEditableTableData(data, this.tableColumns);
        this.resetCellEditing();
      }).finally(() => {
        this.isLoading = false;
      });
    },
    /**
     * [테이블 페이징] 페이지 변경 핸들러
     * @author Ju
     * @param  {Object} pagination 페이지 정보 객체
     */
    handlerPaginationChange(_pagination) {
      this.pagination = _pagination;
    },
    /**
     * [헤더 페이징] 현재 페이지 변경 핸들러
     * @author Ju
     * @param  {Number} currentPage 현재 페이지 번호
     */
    handlerHeaderCurrentChange(_currentPage) {
      this.$refs.emsObjTable.setCurrentPage(_currentPage);
    },
    clickSaveButton() {
      const updatedData = this.getUpdatedDatas(this.tableData, true, ['objId']);
      const target = '관리 대상';
      const action = msgUtil.action.SAVE;

      operationSettingsApi.updateEmsObjList(updatedData).then((response) => {
        const result = response.data;
        if (result.success) {
          this.loadData();
        }
        this.$message(msgUtil.getResultMsgData(action, target, result.success));
      }).catch(() => {
        this.$message(msgUtil.getResultMsgData(action, target, false));
      });
    },
    clickCancelButton() {
      this.loadData();
    },
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    clickDeleteServerButton() {
      const target = '관리 대상';
      const action = msgUtil.action.DELETE;
      this.$confirm(...msgUtil.getBasicConfirmData(action)).then(() => {
        operationSettingsApi.deleteEmsObjList(this.checkedRows).then((response) => {
          const result = response.data;
          if (result.success === true) {
            this.$message(msgUtil.getResultMsgData(action, target, true));
            this.loadData();
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        }).catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
      });
    },
    allCheckChangeButtonClick(objManagementApplyFlag) {
      const result = this.editDirectBulk(this.tableData, 'objManagementApplyFlag', objManagementApplyFlag);
      this.tableData = result.tableData;
      this.completeEditBulk(...result);
    },
    handlerEndEdit() {
      this.checkExistModifiedData(this.tableData);
    },
  },
};
</script>

<style module>
</style>



// WEBPACK FOOTER //
// src/views/settings/operation/ThirdPartySettings/ObjSetting/index.vue